import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { Perche } from "../components/home/Perche";
import { Metodo } from "../components/home/Metodo";
import { CTABand } from "../components/CTABand";
import { useContents } from "../hooks/useContents";

const VALUES = [
  { title: "Concretezza", desc: "Poche promesse, molti fatti: valutiamo ogni lavoro per quello che serve davvero." },
  { title: "Cura del dettaglio", desc: "La differenza tra un lavoro fatto e un lavoro fatto bene sta nei dettagli." },
  { title: "Rispetto", desc: "Rispetto per il cliente, per la sua casa, per i tempi e per gli accordi presi." },
  { title: "Responsabilità", desc: "Ogni cantiere è seguito con attenzione, dalla prima valutazione alla consegna." },
];

export default function Azienda() {
  const c = useContents();
  usePageMeta(
    "Azienda | Costruzioni Ciosko — Impresa Edile in Veneto",
    "Costruzioni Ciosko: impresa edile attiva in Veneto. Costruiamo e ristrutturiamo con attenzione, organizzazione e responsabilità."
  );
  return (
    <>
      <PageHero
        overline="Azienda"
        lines={["Costruire è il nostro mestiere.", "Farlo bene è la nostra priorità."]}
        description="Un'impresa edile moderna e organizzata, attiva in Veneto con particolare attenzione alla provincia di Venezia e alle zone limitrofe."
        image="/images/about.jpg"
      />
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-20">
          <Reveal>
            <div className="steel-frame">
              <div className="aspect-[4/3] overflow-hidden border border-steel">
                <img src="/images/about.jpg" alt="Lavorazione muraria eseguita da Costruzioni Ciosko" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1.2s]" />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="overline-label">La nostra storia</p>
            <div className="mt-6 space-y-4 text-cement leading-relaxed text-[15px] sm:text-base">
              <p>{c("about_p1", "Costruzioni Ciosko nasce dalla passione per il lavoro e dalla volontà di offrire ai nostri clienti un servizio concreto, professionale e affidabile.")}</p>
              <p>{c("about_p2", "Da generazioni ci occupiamo di costruire, ristrutturare e dare forma agli spazi in cui le persone vivono e lavorano.")}</p>
              <p>{c("about_p3", "Ogni cantiere viene seguito con attenzione, organizzazione e responsabilità, mettendo al centro la qualità del risultato e la soddisfazione del cliente.")}</p>
              <p>
                Lavoriamo in tutto il Veneto, con particolare attenzione alla provincia di Venezia e alle zone
                limitrofe, per privati, aziende e operatori del settore.
              </p>
            </div>
            <Link to="/contatti" data-testid="azienda-quote-button" className="btn-primary mt-8">
              Parliamone di persona <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
      <section className="py-20 lg:py-28 bg-graphite border-y border-steel">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <p className="overline-label flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-forest-mid" aria-hidden="true" /> I nostri valori
          </p>
          <h2 className="h-display text-2xl sm:text-3xl lg:text-4xl mt-5 text-white max-w-3xl">Quello in cui crediamo</h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.07}>
                <div data-testid={`value-${v.title.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-")}`} className="h-full bg-card border border-steel p-7 hover:border-forest-mid transition-colors duration-300">
                  <CheckCircle2 size={20} className="text-forest-glow" />
                  <h3 className="font-display font-bold text-lg uppercase tracking-tight text-white mt-4">{v.title}</h3>
                  <p className="text-sm text-cement leading-relaxed mt-3">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <Perche />
      <Metodo />
      <CTABand />
    </>
  );
}
